import ButtonGroupCustomized from '@/components/ButtonGroupCustomized'
import Paper from '@/components/Paper'
import { Skeleton } from '@/components/ui/skeleton'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import {
    getTechnicalTicketSummary,
    getTechnicalTickets,
    getUnassignedTickets,
} from '@/modules/Dashboard/services'
import { TechnicalTicket } from '@/modules/Dashboard/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { formatDateWithFormatter } from '@/utils/function'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Typogaphy } from 'vnetwork-ui'
import { ArrowRight, AlertTriangle, AlertCircle } from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'

type TicketTab = 'work-in-progress' | 'completed' | 'closed' | 'unassigned'
type ViewMode = 'chart' | 'list'

const STATUS_COLORS = {
    in_progress: '#3b82f6',
    completed: '#22c55e',
    closed: '#6b7280',
    unassigned: '#f59e0b',
    overdue: '#ef4444',
    high_priority: '#f97316',
}

const TechnicalTicketsWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const [viewMode, setViewMode] = useState<ViewMode>('chart')
    const [activeTab, setActiveTab] = useState<TicketTab>('work-in-progress')

    // Get summary for tab counts
    const { data: summaryData, isLoading: isLoadingSummary } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.TECHNICAL_TICKET_SUMMARY],
            queryFn: getTechnicalTicketSummary,
        })

    // Get tickets based on active tab
    const { data: ticketsData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TECHNICAL_TICKETS, activeTab],
        queryFn: () =>
            activeTab === 'unassigned'
                ? getUnassignedTickets({ page: 1, page_size: 10 })
                : getTechnicalTickets({
                      status: activeTab as
                          | 'work-in-progress'
                          | 'completed'
                          | 'closed',
                      page: 1,
                      page_size: 10,
                  }),
    })

    const summary = summaryData?.result

    const tabs = [
        {
            key: 'work-in-progress' as TicketTab,
            label: t('technical.tab_in_progress'),
            count: summary?.in_progress_count || 0,
        },
        {
            key: 'completed' as TicketTab,
            label: t('technical.tab_completed'),
            count: summary?.completed_count || 0,
        },
        {
            key: 'closed' as TicketTab,
            label: t('technical.tab_closed'),
            count: summary?.closed_count || 0,
        },
        {
            key: 'unassigned' as TicketTab,
            label: t('technical.tab_unassigned'),
            count: summary?.unassigned_count || 0,
        },
    ]

    const handleTicketClick = (ticketId: number) => {
        navigate(generatePathDatacentral(paths.ticketDetail, { id: ticketId }))
    }

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'high':
                return 'bg-red-100 text-red-700'
            case 'medium':
                return 'bg-orange-100 text-orange-700'
            default:
                return 'bg-gray-100 text-gray-700'
        }
    }

    const isOverdue = (ticket: TechnicalTicket) => {
        if (!ticket.ticket_estimated_completion_date) return false
        return dayjs().isAfter(dayjs(ticket.ticket_estimated_completion_date))
    }

    // Strip HTML tags for plain text display
    const stripHtml = (html: string) => {
        const tmp = document.createElement('div')
        tmp.innerHTML = html
        return tmp.textContent || tmp.innerText || ''
    }

    const stateComponent = renderState()

    // Chart data for pie chart - order matches the visual in screenshot
    const chartData = [
        {
            name: t('technical.unassigned'),
            value: summary?.unassigned_count || 0,
            color: STATUS_COLORS.unassigned,
        },
        {
            name: t('technical.in_progress'),
            value: summary?.in_progress_count || 0,
            color: STATUS_COLORS.in_progress,
        },
        {
            name: t('technical.completed'),
            value: summary?.completed_count || 0,
            color: STATUS_COLORS.completed,
        },
        {
            name: t('technical.closed'),
            value: summary?.closed_count || 0,
            color: STATUS_COLORS.closed,
        },
    ]

    const totalTickets = chartData.reduce((sum, item) => sum + item.value, 0)

    const viewModeButtons = [
        {
            label: t('technical.view_chart'),
            value: 'chart',
        },
        {
            label: t('technical.view_list'),
            value: 'list',
        },
    ]

    // Legend keys for data-tour
    const legendKeys = ['unassigned', 'in_progress', 'completed', 'closed']

    // Custom legend renderer
    const renderCustomLegend = () => (
        <div
            className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2"
            data-tour="ticket-chart-legend"
        >
            {chartData.map((item, index) => (
                <div
                    key={index}
                    className="flex items-center gap-1.5"
                    data-tour={`ticket-status-${legendKeys[index]}`}
                >
                    <div
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: item.color }}
                    />
                    <span className="text-xs text-gray-600">{item.name}</span>
                </div>
            ))}
        </div>
    )

    return (
        <Paper className="h-full">
            <div className="h-full flex flex-col">
                {/* Header with view toggle */}
                <div className="flex justify-between mb-2 flex-shrink-0 flex-col gap-2">
                    <Typogaphy className="font-semibold text-primary capitalize">
                        {t('technical.tickets_title')}
                    </Typogaphy>
                    <ButtonGroupCustomized
                        className="p-0.5"
                        active={viewMode}
                        onChange={(value) => setViewMode(value as ViewMode)}
                        buttons={viewModeButtons}
                    />
                </div>

                {viewMode === 'chart' ? (
                    /* Chart View */
                    <div className="flex-1 min-h-0 flex flex-col">
                        {isLoadingSummary ? (
                            <div className="flex-1 flex items-center justify-center">
                                <Skeleton className="w-40 h-40 rounded-full" />
                            </div>
                        ) : (
                            <>
                                {/* Pie Chart with center text */}
                                <div
                                    className="relative"
                                    style={{ height: '200px' }}
                                >
                                    <ResponsiveContainer
                                        width="100%"
                                        height="100%"
                                    >
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={55}
                                                outerRadius={85}
                                                paddingAngle={2}
                                                dataKey="value"
                                                startAngle={90}
                                                endAngle={-270}
                                                label={({
                                                    cx,
                                                    cy,
                                                    midAngle,
                                                    innerRadius,
                                                    outerRadius,
                                                    value,
                                                }) => {
                                                    if (value === 0) return null
                                                    const RADIAN = Math.PI / 180
                                                    const radius =
                                                        innerRadius +
                                                        (outerRadius -
                                                            innerRadius) *
                                                            0.5
                                                    const x =
                                                        cx +
                                                        radius *
                                                            Math.cos(
                                                                -(
                                                                    midAngle ||
                                                                    0
                                                                ) * RADIAN || 0
                                                            )
                                                    const y =
                                                        cy +
                                                        radius *
                                                            Math.sin(
                                                                -(
                                                                    midAngle ||
                                                                    0
                                                                ) * RADIAN
                                                            )
                                                    return (
                                                        <text
                                                            x={x}
                                                            y={y}
                                                            fill="#fff"
                                                            textAnchor="middle"
                                                            dominantBaseline="central"
                                                            fontSize={11}
                                                            fontWeight="bold"
                                                        >
                                                            {value}
                                                        </text>
                                                    )
                                                }}
                                                labelLine={false}
                                            >
                                                {chartData.map(
                                                    (entry, index) => (
                                                        <Cell
                                                            key={`cell-${index}`}
                                                            fill={entry.color}
                                                        />
                                                    )
                                                )}
                                            </Pie>
                                            <Tooltip
                                                formatter={(
                                                    value: number | undefined
                                                ) => [
                                                    value || 0,
                                                    value,
                                                    t('technical.tickets'),
                                                ]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '12px',
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    {/* Center text */}
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="text-center">
                                            <div className="text-3xl font-bold text-gray-800">
                                                {totalTickets}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                {t('technical.total')}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Custom Legend */}
                                <div className="flex-shrink-0">
                                    {renderCustomLegend()}
                                </div>

                                {/* Alert Stats */}
                                <div className="flex gap-3 mt-4 flex-shrink-0">
                                    <div
                                        className="flex items-center gap-2 px-4 py-3 bg-red-50 rounded-lg flex-1"
                                        data-tour="ticket-alert-overdue"
                                    >
                                        <AlertTriangle className="w-5 h-5 text-red-500" />
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-xl font-bold text-red-500">
                                                {summary?.overdue_count || 0}
                                            </span>
                                            <span className="text-sm text-red-500">
                                                {t('technical.overdue')}
                                            </span>
                                        </div>
                                    </div>
                                    <div
                                        className="flex items-center gap-2 px-4 py-3 bg-orange-50 rounded-lg flex-1"
                                        data-tour="ticket-alert-high-priority"
                                    >
                                        <AlertCircle className="w-5 h-5 text-orange-500" />
                                        <div className="flex items-baseline gap-1.5">
                                            <span className="text-xl font-bold text-orange-500">
                                                {summary?.high_priority_in_progress ||
                                                    0}
                                            </span>
                                            <span className="text-sm text-orange-500">
                                                {t('technical.high_priority')}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* View All Button */}
                                <div className="text-center pt-3 flex-shrink-0 border-t mt-4">
                                    <button
                                        onClick={() =>
                                            navigate(
                                                generatePathDatacentral(
                                                    paths.ticket,
                                                    {}
                                                )
                                            )
                                        }
                                        className="text-sm text-primary hover:underline font-medium"
                                    >
                                        {t('widgets.view_all')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                    /* List View */
                    <>
                        {/* Compact Summary Stats */}
                        <div className="flex-shrink-0 mb-3">
                            {isLoadingSummary ? (
                                <div className="flex gap-2">
                                    <Skeleton className="h-10 flex-1 rounded-lg" />
                                    <Skeleton className="h-10 flex-1 rounded-lg" />
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg flex-1">
                                        <AlertTriangle className="w-4 h-4 text-red-600" />
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-lg font-bold text-red-600">
                                                {summary?.overdue_count || 0}
                                            </span>
                                            <span className="text-xs text-red-600">
                                                {t('technical.overdue')}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 rounded-lg flex-1">
                                        <AlertCircle className="w-4 h-4 text-orange-600" />
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-lg font-bold text-orange-600">
                                                {summary?.high_priority_in_progress ||
                                                    0}
                                            </span>
                                            <span className="text-xs text-orange-600">
                                                {t('technical.high_priority')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Tabs */}
                        <div className="flex-shrink-0 mb-3">
                            <ButtonGroupCustomized
                                className="p-1"
                                active={activeTab}
                                onChange={(value) =>
                                    setActiveTab(value as TicketTab)
                                }
                                buttons={tabs.map((tab) => ({
                                    label: tab.label,
                                    value: tab.key,
                                    count: tab.count,
                                }))}
                            />
                        </div>

                        {/* Ticket List - Fixed 3 items */}
                        <div className="space-y-2">
                            {stateComponent ? (
                                <>
                                    {[1, 2, 3].map((i) => (
                                        <Skeleton
                                            key={i}
                                            className="h-[72px] rounded-lg"
                                        />
                                    ))}
                                </>
                            ) : (
                                <>
                                    {/* Show max 3 tickets */}
                                    {ticketsData?.results
                                        ?.slice(0, 3)
                                        .map((ticket: TechnicalTicket) => (
                                            <div
                                                key={ticket.ticket_id}
                                                onClick={() =>
                                                    handleTicketClick(
                                                        ticket.ticket_id
                                                    )
                                                }
                                                className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors h-[72px]"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-0.5">
                                                        <span className="text-sm font-medium text-primary">
                                                            #{ticket.ticket_id}
                                                        </span>
                                                        <span
                                                            className={`text-[10px] px-1.5 py-0.5 rounded ${getPriorityColor(ticket.ticket_priority)}`}
                                                        >
                                                            {t(
                                                                `technical.priority_${ticket.ticket_priority}`
                                                            )}
                                                        </span>
                                                        {isOverdue(ticket) && (
                                                            <span className="flex items-center gap-0.5 text-[10px] text-red-600">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                {t(
                                                                    'technical.overdue'
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-900 truncate">
                                                        {stripHtml(
                                                            ticket.ticket_content
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[10px] text-gray-500 mt-0.5">
                                                        <span>
                                                            {
                                                                ticket.customer_name
                                                            }
                                                        </span>
                                                        <span>·</span>
                                                        <span>
                                                            {
                                                                ticket.product_group_name
                                                            }
                                                        </span>
                                                        <span>·</span>
                                                        <span>
                                                            {formatDateWithFormatter(
                                                                ticket.ticket_requested_at,
                                                                'DD/MM HH:mm'
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                                <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            </div>
                                        ))}
                                    {/* Empty placeholders to maintain 3 rows */}
                                    {Array.from({
                                        length: Math.max(
                                            0,
                                            3 -
                                                (ticketsData?.results?.length ||
                                                    0)
                                        ),
                                    }).map((_, i) => (
                                        <div
                                            key={`empty-${i}`}
                                            className="flex items-center justify-center p-2.5 bg-gray-50 rounded-lg h-[72px] border border-dashed border-gray-200"
                                        >
                                            <span className="text-xs text-gray-400">
                                                {ticketsData?.results
                                                    ?.length === 0 && i === 1
                                                    ? t('technical.no_tickets')
                                                    : ''}
                                            </span>
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>

                        {/* View All Button */}
                        {(ticketsData?.results?.length || 0) > 0 && (
                            <div className="text-center pt-2 flex-shrink-0 border-t mt-2">
                                <button
                                    onClick={() =>
                                        navigate(
                                            generatePathDatacentral(
                                                paths.ticket,
                                                {}
                                            )
                                        )
                                    }
                                    className="text-xs text-primary hover:underline"
                                >
                                    {t('widgets.view_all')} (
                                    {ticketsData?.pagination?.total || 0})
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Paper>
    )
}

export default TechnicalTicketsWidget
